import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { orders as ordersApi } from "../api/client";
import type { OrderState, OrderSummary } from "../api/types";
import { useMe } from "../auth/session";
import { companyCanWrite } from "../auth/session";
import { Page } from "../components/Shell";
import { Empty, ErrorBox, Icon, Pill, Spinner, StatePill } from "../components/ui";
import { useAsync } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { dateTimeShort } from "../lib/format";
import { brl, usd } from "../lib/money";

const STATES: OrderState[] = ["quoted", "awaiting_funds", "funds_received", "executing", "sent", "settled", "under_review", "failed", "refunded", "expired"];
const PAGE_SIZE_FALLBACK = 20;

/** Lista completa de pedidos da empresa com filtros na URL (compartilháveis) e paginação do servidor. */
export function Pagamentos() {
  const me = useMe();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const state = sp.get("status") ?? "";
  const from = sp.get("de") ?? "";
  const to = sp.get("ate") ?? "";
  const page = Math.max(1, Number(sp.get("pagina") ?? "1") || 1);
  const qParam = sp.get("q") ?? "";
  const [q, setQ] = useState(qParam);

  // busca com atraso curto para não bater na API a cada tecla
  useEffect(() => {
    const id = window.setTimeout(() => { if (q !== qParam) patch({ q, pagina: "1" }); }, 350);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  function patch(next: Record<string, string>) {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(next)) { if (v) n.set(k, v); else n.delete(k); }
    setSp(n, { replace: true });
  }

  const { data, error, loading, reload } = useAsync(
    (signal) => ordersApi.list({ state: state || undefined, from: from || undefined, to: to || undefined, q: qParam || undefined, page }).then((r) => ({ ...r, signal })),
    [state, from, to, qParam, page],
  );
  const items: OrderSummary[] = data?.items ?? [];
  const pageSize = data?.pageSize ?? PAGE_SIZE_FALLBACK;
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));
  const hasFilters = Boolean(state || from || to || qParam);
  const canWrite = companyCanWrite(me);
  const exportUrl = useMemo(() => ordersApi.exportCsvUrl({ from: from || undefined, to: to || undefined }), [from, to]);

  return (
    <Page
      title={t.payments.title}
      actions={canWrite ? (
        <Link to="/pagamentos/novo" className="btn">
          {Icon.plus()}
          {t.panel.newPayment}
        </Link>
      ) : undefined}
    >
      <div className="card">
        <div className="card-head" style={{ flexWrap: "wrap", gap: 10 }}>
          <div className="inp" style={{ minHeight: 38, width: 300, maxWidth: "100%" }}>
            <input aria-label={t.payments.searchPh} placeholder={t.payments.searchPh} value={q} onChange={(e) => setQ(e.target.value)} style={{ fontSize: 13 }} />
          </div>
          <div className="inp" style={{ minHeight: 38 }}>
            <select aria-label={t.payments.status} value={state} onChange={(e) => patch({ status: e.target.value, pagina: "1" })} style={{ fontSize: 13 }}>
              <option value="">{t.payments.allStatuses}</option>
              {STATES.map((s) => <option key={s} value={s}>{t.states[s] ?? s}</option>)}
            </select>
          </div>
          <div className="inp" style={{ minHeight: 38 }}><input aria-label={t.payments.from} type="date" value={from} onChange={(e) => patch({ de: e.target.value, pagina: "1" })} style={{ fontSize: 13 }} /></div>
          <div className="inp" style={{ minHeight: 38 }}><input aria-label={t.payments.to} type="date" value={to} onChange={(e) => patch({ ate: e.target.value, pagina: "1" })} style={{ fontSize: 13 }} /></div>
          {hasFilters && (
            <button type="button" className="btn ghost sm" onClick={() => { setQ(""); setSp(new URLSearchParams(), { replace: true }); }}>{t.payments.clear}</button>
          )}
          <span style={{ flex: 1 }} />
          <Pill tone="mute">{fmt(t.payments.total, { n: data?.total ?? 0 })}</Pill>
          <a className="btn ghost sm" href={exportUrl} download title={t.payments.exportHint}>
            {Icon.download()}
            {t.common.export}
          </a>
        </div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : items.length === 0 ? (
          <Empty body={hasFilters ? t.payments.empty : t.panel.emptyBody} />
        ) : (
          <>
            <div className="tscroll desk-table" tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    <th>{t.payments.colId}</th>
                    <th>{t.payments.colSupplier}</th>
                    <th>{t.payments.colUsd}</th>
                    <th>{t.payments.colBrl}</th>
                    <th>{t.payments.colStatus}</th>
                    <th>{t.payments.colDate}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((o) => (
                    <tr key={o.id} className="rowlink" onClick={() => navigate(`/pagamentos/${o.id}`)}>
                      <td><Link to={`/pagamentos/${o.id}`} className="mono" style={{ fontSize: 13 }}>{o.id}</Link></td>
                      <td>
                        <div className="cell2">
                          <b>{o.beneficiary?.legalName ?? o.beneficiaryName ?? "—"}</b>
                          {o.reference && <span>{o.reference}</span>}
                        </div>
                      </td>
                      <td className="mono">{usd(o.usdCents)}</td>
                      <td className="mono">{brl(o.totalCents)}</td>
                      <td><StatePill state={o.state} /></td>
                      <td className="ink2">{dateTimeShort(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mob-list">
              {items.map((o) => (
                <Link key={o.id} to={`/pagamentos/${o.id}`} className="item" style={{ textDecoration: "none", color: "inherit", borderBottom: "1px solid var(--surface-2)" }}>
                  <div className="l">
                    <b>{o.beneficiary?.legalName ?? o.beneficiaryName ?? "—"}</b>
                    <span className="mono">{o.id} · {dateTimeShort(o.createdAt)}</span>
                  </div>
                  <div className="r">
                    <span className="mono">{usd(o.usdCents)}</span>
                    <StatePill state={o.state} />
                  </div>
                </Link>
              ))}
            </div>
            {pages > 1 && (
              <div className="row between" style={{ padding: "12px 20px", borderTop: "1px solid var(--surface-2)" }}>
                <button type="button" className="btn ghost sm" disabled={page <= 1} onClick={() => patch({ pagina: String(page - 1) })}>{t.payments.prev}</button>
                <span className="muted small">{fmt(t.payments.pageOf, { p: page, n: pages })}</span>
                <button type="button" className="btn ghost sm" disabled={page >= pages} onClick={() => patch({ pagina: String(page + 1) })}>{t.payments.next}</button>
              </div>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
